import { Tooltip } from "antd";
import { useMemo } from "react";
import { TiArrowSortedDown, TiArrowSortedUp, TiEquals } from "react-icons/ti";
import { NumericFormat } from "react-number-format";
import { usePagination, useTable } from "react-table";
import _ from "../../../i18n";
import { getTimeByDurationFormat, toFixed } from "../../../utils";
import { InfoCircleFilledIcon } from "../../common/Icons";
import { EntityIcon } from "../../common/Icons/EntityIcon";
import TTPPaginator from "../../common/TTPPaginator";
import "./TreeTable.local.scss";

export default function TreeTable({ data, erpData, filter, setFilter }) {
  const { page: paginationPage, pageSize, nbResults } = filter;
  const columns = useMemo(
    () => [
      {
        id: "client",
        Header: "Client",
        accessor: (row) => row["data"],
        Cell: ({ value }) => {
          const { id, label, client_recovr_external_id, attachedClients } =
            value;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {label.length > 35 ? (
                <Tooltip placement="right" title={label} mouseLeaveDelay={0}>
                  <p style={{ fontSize: "14px" }}>{`${label.substring(
                    0,
                    35
                  )}...`}</p>
                </Tooltip>
              ) : (
                <p style={{ fontSize: "14px" }}>{label}</p>
              )}
              <div className="import-info">
                <div>
                  {attachedClients && attachedClients.length ? (
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          style={{
                            textAlign: "left",
                          }}
                        >
                          <p>
                            {_("attachedClients")}
                            {" : "}
                          </p>
                          {attachedClients.map((item) => (
                            <p>{"- " + item.clientName}</p>
                          ))}
                        </div>
                      }
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <EntityIcon size={14} color={"#6d7f92"} />
                      </div>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {client_recovr_external_id ? (
                    <a href={client_recovr_external_id} target="_blank">
                      <Tooltip
                        placement="top"
                        title={
                          <div
                            style={{
                              textAlign: "left",
                            }}
                          >
                            Portail client
                          </div>
                        }
                      >
                        <img
                          src="https://static.intercomassets.com/avatars/6472131/square_128/Group_39910-1744282782.png"
                          alt="recovr_logo"
                          width={14}
                        />
                      </Tooltip>
                    </a>
                  ) : (
                    ""
                  )}
                  {erpData &&
                    erpData.clients.map((erp) => {
                      return erp[id] && erp[id].erpId ? (
                        <Tooltip
                          placement="top"
                          title={
                            <div>
                              {erp[id].erpLabel && (
                                <p>
                                  {_("importedFrom")} : {erp[id].erpLabel}
                                </p>
                              )}

                              <p>
                                {_("identifier")} : {erp[id].erpId}
                              </p>
                            </div>
                          }
                        >
                          <InfoCircleFilledIcon size={14} color="#6d7f92" />
                        </Tooltip>
                      ) : (
                        ""
                      );
                    })}
                </div>
              </div>
            </div>
          );
        },
      },

      {
        Header: "Horaire",
        columns: [
          {
            id: "budget",
            Header: `Budget`,
            accessor: (row) => row["data"]["budgetH"],
            Cell: ({ value }) =>
              value && (
                <div>
                  <span
                    style={{
                      marginBottom: "0px",
                      textAlign: "center",
                    }}
                  >
                    <p style={{ fontSize: "14px" }}>
                      {getTimeByDurationFormat(value["total"])}
                    </p>
                  </span>
                  <div
                    className="zone-totals"
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    {value["subTotals"] &&
                      value["subTotals"].map((item) => (
                        <div key={item.zone} style={{ fontSize: "12px" }}>
                          <span>{item.zone}:</span>
                          {getTimeByDurationFormat(toFixed(item.value, 2))}
                        </div>
                      ))}
                  </div>
                </div>
              ),
          },
          {
            id: "time",
            Header: `Réalisé`,
            accessor: (row) => row["data"]["PrestaTs"],
            Cell: ({ value }) =>
              value && (
                <div>
                  <div className="cell_container_budget_hr">
                    <span
                      style={{
                        marginBottom: "0px",
                        textAlign: "center",
                      }}
                    >
                      <p style={{ fontSize: "14px" }}>
                        {getTimeByDurationFormat(value["total"])}
                      </p>
                    </span>
                    <span
                      className="diff-badge"
                      style={{
                        "--color": value["diff"] <= 0 ? "#02af8e" : "#de4848",
                        "--bg-color":
                          value["diff"] <= 0 ? "#02af8e25" : "#de484825",
                      }}
                    >
                      <NumericFormat
                        value={value["diff"]}
                        displayType={"text"}
                        thousandSeparator={"."}
                        decimalSeparator={","}
                        suffix=" %"
                      />
                    </span>
                  </div>
                  <div
                    className="zone-totals"
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                    }}
                  >
                    {value["subTotals"] &&
                      value["subTotals"].map((item) => (
                        <div key={item.zone} style={{ fontSize: "12px" }}>
                          <span>{item.zone}:</span>
                          {getTimeByDurationFormat(toFixed(item.value, 2))}
                        </div>
                      ))}
                  </div>
                </div>
              ),
          },
        ],
      },

      {
        Header: "Economique",
        columns: [
          {
            id: "economic",
            Header: `Budget`,
            accessor: (row) => row["data"]["budgetEco"],
            Cell: ({ value, row: { index }, column: { id } }) =>
              value && (
                <div className="cell_container">
                  <NumericFormat
                    value={value["total"].toFixed(2)}
                    displayType={"text"}
                    thousandSeparator={"."}
                    decimalSeparator={","}
                    prefix="€ "
                  />
                  <div className="sub_cell">
                    <p>{`${_("budgetRate")}: `}</p>
                    <NumericFormat
                      value={value["ratio"]}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      suffix="€/h"
                    />
                  </div>
                </div>
              ),
          },
          {
            id: "profit",
            Header: `Réalisé`,
            accessor: (row) => row["data"]["profit"],
            Cell: ({ value }) =>
              value && (
                <div className="cell_container">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <NumericFormat
                      value={value["revenue"].toFixed(2)}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      prefix="€ "
                    />

                    {value["diff"] > 0 ? (
                      <TiArrowSortedUp color="#02af8e" size={15} />
                    ) : value["diff"] < 0 ? (
                      <TiArrowSortedDown color="#de4848" size={15} />
                    ) : (
                      <TiEquals color="#ea8124" size={12} />
                    )}
                  </div>
                  <div className="sub_cell">
                    <p>{`${_("effectifRate")}: `}</p>
                    <NumericFormat
                      value={value["ratio"]}
                      displayType={"text"}
                      thousandSeparator={"."}
                      decimalSeparator={","}
                      suffix="€/h"
                    />
                  </div>
                </div>
              ),
          },
        ],
      },
      {
        id: "minBilling",
        Header: _("minimumBilling"),
        accessor: (row) => row["data"]["minBilling"],
        Cell: ({ value }) =>
          value && (
            <div className="cell_container">
              <NumericFormat
                value={Math.round(value["total"])}
                displayType={"text"}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="€ "
              />
            </div>
          ),
      },
      {
        id: "encours",
        Header: "Encours",
        accessor: (row) => row["data"]["Encours"],
        Cell: ({ value }) =>
          value && (
            <div className="cell_container">
              <NumericFormat
                value={value["amount"].toFixed(2)}
                displayType={"text"}
                thousandSeparator={"."}
                decimalSeparator={","}
                prefix="€ "
              />
            </div>
          ),
      },
    ],
    [filter.year]
  );

  const { getTableProps, headerGroups, prepareRow } = useTable(
    {
      columns,
      data: data.result || [],
      initialState: { pageIndex: paginationPage, pageSize: pageSize },
      manualPagination: true,
      pageCount: Math.ceil(nbResults / paginationPage),
    },
    usePagination
  );

  const { rows } = useTable(
    {
      columns,
      data: data.result || [],
    },
    usePagination
  );

  return (
    <div className="ttp-tab-container" style={{ padding: "10px 0", margin: 0 }}>
      <table {...getTableProps()} className="tree_table">
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()} style={{}}>
                  {column.render("Header")}
                </th>
              ))}
            </tr>
          ))}
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr style={{ height: "72px" }} {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  );
                })}
              </tr>
            );
          })}
        </thead>
      </table>
      <TTPPaginator
        onPageChange={({ selected }) => {
          const newPage = selected + 1;

          if (paginationPage !== newPage) {
            setFilter((prev) => ({
              ...prev,
              page: newPage,
            }));
          }
        }}
        pageSize={pageSize}
        nbResult={nbResults}
        paginationPage={paginationPage}
      />
    </div>
  );
}
